import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import {
  Box,
  Button,
  Divider,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { ExpandLess, ExpandMore } from '@material-ui/icons'
import { LoadingFeedback } from 'components'

import {
  NoFilterDataDialog,
  ExportRopaDialog,
  StatusFilter,
} from './components'

import useAuth from 'hooks/useAuth'
import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'
import { Controller, useForm } from 'react-hook-form'
import { isEmpty } from 'lodash'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'
import schema from './schema'
import useStyles from './styles'
import countText from 'helpers/functions/countText'
import dataTreatmentAgents from 'constants/dataTreatmentAgents'

const RipdForm = ({
  ropa,
  ripd,
  setTriggerValidation,
  setDisableGenerateButton,
}) => {
  const classes = useStyles()
  const { company, setFreeTrialBlock } = useAuth()
  const snackbar = useSnackbar()

  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [willGetData, setWillGetData] = useState(true)
  const [openNoFilterDialog, setOpenNoFilterDialog] = useState(false)
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)
  const [formData, setFormData] = useState(null)

  const { response: riskResponse, isLoading: loadingRisk } = useFetch(
    service.dponet.matrixRisk.list,
    {
      byProcessRiskAssignment: !ropa,
      minimal: true,
    },
    [],
  )

  const riskOptions = loadingRisk ? [] : riskResponse?.data?.matrixRisks || []

  const riskFilterOptions = [
    { id: 'no_risk', name: 'Sem Risco' },
    ...riskOptions,
  ]

  const { response, isLoading } = useFetch(
    service.dponet.departments.byProcess,
    {
      byProcessRiskAssignment: !ropa,
    },
    [willGetData],
    open && willGetData && !loadingRisk,
  )

  const { response: responseLegalFrameworks } = useFetch(
    service.dponet.legalFrameworks.listByCompany,
    null,
    [willGetData],
    open && willGetData,
  )

  const legalFrameworks = (
    responseLegalFrameworks?.data?.legalFrameworks || []
  ).map((item) => ({
    id: item.id,
    name: `${item.name} - ${
      constants.legalFrameworks.LABEL_BY_TYPE[item.dataType]
    }`,
  }))

  const { handleSubmit, control, errors, reset, watch, triggerValidation } =
    useForm({
      validationSchema: schema(
        company?.statusId !== constants.userCompanies.TRIAL_LIMITED_STATUS &&
          ripd,
      ),
      defaultValues: {
        justificationReport: '',
        comment: '',
        matrixRisks: [],
        departments: [],
        legalFrameworks: [],
        dataTreatmentAgents: [],
        statuses: [],
        anotherJustificationReport: '',
      },
    })

  useEffect(
    () => {
      setTriggerValidation(() => triggerValidation)
    },
    //eslint-disable-next-line
    [],
  )

  const handleClick = () => {
    if (open && willGetData) setWillGetData(false)
    setOpen((open) => !open)
  }

  const handleRipdRopa = (data) => {
    if (ropa) {
      setFormData(data)
      return handleOpenExportDialog()
    }

    return onSubmit(data)
  }

  const handleOpenExportDialog = () => setIsExportDialogOpen(true)
  const handleCloseExportDialog = () => setIsExportDialogOpen(false)

  const onSubmit = async (dialogData) => {
    const data = formData ? { ...formData, ...dialogData } : dialogData
    if (company?.statusId === constants.userCompanies.TRIAL_LIMITED_STATUS) {
      return setFreeTrialBlock(true)
    }

    try {
      setLoading(true)
      const response = await service.dponet.ripds.exportsRipd({
        ripds: mountPayload(data),
      })

      snackbar.open({
        message:
          response?.data?.message ||
          'Seu relatório será gerado e enviado ao seu e-mail. Pode demorar alguns minutos.',
        variant: response?.status === 200 ? 'success' : 'warning',
      })
      reset()
      handleCloseExportDialog()
    } catch (error) {
      if (error?.response?.status === 404) {
        setOpenNoFilterDialog(true)
        setDisableGenerateButton(true)
      } else {
        snackbar.open({
          message:
            helpers.formatters.errorMessage(
              error?.response?.data?.error,
              false,
            ) ||
            `Não foi possível gerar o relatório ${ropa ? 'RoPA' : 'RIPD'}.`,
          variant: 'error',
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const mountPayload = (data) => {
    const justificationReport =
      data?.justificationReport === 'another'
        ? data?.anotherJustificationReport
        : data?.justificationReport
    const matrixRiskIds = data?.matrixRisks?.map((risk) => risk.id)
    const departments = data?.departments?.map((department) => department.id)
    const legalFrameworks = data?.legalFrameworks?.map(
      (legal_framework) => legal_framework.id,
    )
    const agentType = data?.dataTreatmentAgents?.map((agent) => agent.id)
    const statuses = (data?.statuses || []).flatMap(
      (status) => status.statusIds,
    )

    return {
      comment: data?.comment,
      anotherJustificationReport: data?.anotherJustificationReport,
      justificationReport,
      reportType: ropa ? 'ropa' : 'ripd',
      format: data?.fileType || 'pdf',
      filters: {
        ...(!isEmpty(matrixRiskIds) && { matrixRiskIds }),
        departmentId: departments,
        legalFrameworks,
        agentType,
        statuses,
      },
    }
  }

  const anotherJustification = watch('justificationReport') === 'another'
  const justificationReport = watch('justificationReport')

  return (
    <Box>
      <LoadingFeedback open={(open && isLoading) || loading} />
      <form id="report-form" onSubmit={handleSubmit(handleRipdRopa)}>
        <Grid container spacing={3}>
          {ripd && (
            <>
              <Grid item xs={12}>
                <InputLabel className={classes.label}>
                  Justificativas para geração do relatório*
                </InputLabel>
                <Typography variant="caption" className={classes.subLabel}>
                  A justificativa selecionada não irá alterar as informações
                  contidas no relatório.
                </Typography>
                <Controller
                  control={control}
                  name="justificationReport"
                  mode="onChange"
                  error={!!errors.justificationReport}
                  as={
                    <Select
                      IconComponent={ExpandMore}
                      className={classes.input}
                      variant="outlined"
                      fullWidth
                      placement="bottom"
                      displayEmpty
                    >
                      {constants?.ripdRisk?.JUSTIFICATION?.map((item) => (
                        <MenuItem key={item?.name} value={item?.name}>
                          <Tooltip title={item?.name} placement="bottom">
                            <Typography className={classes.menuItem}>
                              {item?.name}
                            </Typography>
                          </Tooltip>
                        </MenuItem>
                      ))}
                      <MenuItem key="another" value="another">
                        <Typography className={classes.menuItem}>
                          Outros
                        </Typography>
                      </MenuItem>
                    </Select>
                  }
                />
                {!!errors.justificationReport && (
                  <FormHelperText error className={classes.textHelper}>
                    {errors.justificationReport.message}
                  </FormHelperText>
                )}
                {anotherJustification ? (
                  <Controller
                    control={control}
                    mode="onChange"
                    name="anotherJustificationReport"
                    error={!!errors.anotherJustificationReport}
                    helperText={countText(
                      errors?.anotherJustificationReport?.message,
                      watch('anotherJustificationReport')?.length,
                      constants?.ripdRisk?.CHARACTER_LIMIT,
                    )}
                    as={
                      <TextField
                        className={clsx(classes.extraInput, classes.textInput)}
                        variant="outlined"
                        multiline
                        minRows={3}
                        maxRows={10}
                        placeholder="Descreva a outra justificativa"
                        fullWidth
                      />
                    }
                  />
                ) : (
                  justificationReport && (
                    <Box mt={1}>
                      <Typography className={classes.alert}>
                        {helpers?.ripds?.descriptionReport(
                          justificationReport,
                          ropa,
                        )}
                      </Typography>
                    </Box>
                  )
                )}
              </Grid>
              <Grid item xs={12}>
                <Box display="flex">
                  <InputLabel className={classes.label}>Comentário</InputLabel>
                  <Typography className={classes.subLabel}>
                    (opcional)
                  </Typography>
                </Box>
                <Controller
                  control={control}
                  name="comment"
                  mode="onChange"
                  error={!!errors.comment}
                  helperText={countText(
                    errors?.comment?.message,
                    watch('comment')?.length,
                    constants?.ripdRisk?.CHARACTER_LIMIT,
                  )}
                  as={
                    <TextField
                      className={classes.textInput}
                      variant="outlined"
                      multiline
                      minRows={3}
                      maxRows={10}
                      fullWidth
                    />
                  }
                />
              </Grid>
            </>
          )}
          <>
            <Grid item xs={12}>
              {open && (
                <Box pb={3}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Box display="flex">
                        <InputLabel className={classes.label}>
                          Riscos de processos
                        </InputLabel>
                        <Typography className={classes.subLabel}>
                          (opcional)
                        </Typography>
                      </Box>
                      <Controller
                        control={control}
                        name="matrixRisks"
                        mode="onChange"
                        onChange={([, data]) => {
                          setDisableGenerateButton(false)
                          return data
                        }}
                        as={
                          <Autocomplete
                            popupIcon={<ExpandMore />}
                            className={classes.input}
                            multiple
                            options={riskFilterOptions}
                            getOptionLabel={(option) => option.name || ''}
                            getOptionSelected={(option, value) =>
                              option.id === value.id
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                error={!!errors.matrixRisks}
                                helperText={errors?.matrixRisks?.message}
                              />
                            )}
                          />
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex">
                        <InputLabel className={classes.label}>
                          Agente de Tratamento
                        </InputLabel>
                        <Typography className={classes.subLabel}>
                          (opcional)
                        </Typography>
                      </Box>
                      <Controller
                        control={control}
                        name="dataTreatmentAgents"
                        onChange={([, data]) => {
                          setDisableGenerateButton(false)
                          return data
                        }}
                        mode="onChange"
                        as={
                          <Autocomplete
                            popupIcon={<ExpandMore />}
                            className={classes.input}
                            multiple
                            options={dataTreatmentAgents.OPTIONS_TYPES ?? []}
                            getOptionLabel={(option) => option.name}
                            getOptionSelected={(option, value) =>
                              option.id === value.id || value.id === 0
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                error={!!errors?.legalFrameworks}
                                helperText={errors?.legalFrameworks?.message}
                              />
                            )}
                          />
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box display="flex">
                        <InputLabel className={classes.label}>
                          Departamento(s)
                        </InputLabel>
                        <Typography className={classes.subLabel}>
                          (opcional)
                        </Typography>
                      </Box>
                      <Controller
                        control={control}
                        name="departments"
                        onChange={([, data]) => {
                          setDisableGenerateButton(false)
                          return data
                        }}
                        mode="onChange"
                        as={
                          <Autocomplete
                            popupIcon={<ExpandMore />}
                            className={classes.input}
                            multiple
                            options={response?.data?.departments ?? []}
                            getOptionLabel={(option) => option.name}
                            getOptionSelected={(option, value) =>
                              option.id === value.id || value.id === 0
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                error={!!errors.departments}
                                helperText={errors?.departments?.message}
                              />
                            )}
                          />
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex">
                        <InputLabel className={classes.label}>
                          Hipótese Legal
                        </InputLabel>
                        <Typography className={classes.subLabel}>
                          (opcional)
                        </Typography>
                      </Box>
                      <Controller
                        control={control}
                        name="legalFrameworks"
                        onChange={([, data]) => {
                          setDisableGenerateButton(false)
                          return data
                        }}
                        mode="onChange"
                        as={
                          <Autocomplete
                            popupIcon={<ExpandMore />}
                            className={classes.input}
                            multiple
                            options={legalFrameworks}
                            getOptionLabel={(option) => option.name}
                            getOptionSelected={(option, value) =>
                              option.id === value.id || value.id === 0
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                error={!!errors?.legalFrameworks}
                                helperText={errors?.legalFrameworks?.message}
                              />
                            )}
                          />
                        }
                      />
                    </Grid>
                    <StatusFilter
                      control={control}
                      errors={errors}
                      onSelect={() => setDisableGenerateButton(false)}
                    />
                  </Grid>
                </Box>
              )}
              <Button
                variant="text"
                startIcon={open ? <ExpandLess /> : <ExpandMore />}
                onClick={handleClick}
                color="primary"
              >
                {open ? 'Ocultar' : 'Filtros'}
              </Button>
              <Divider className={classes.divider} />
            </Grid>
          </>
        </Grid>
        <ExportRopaDialog
          open={isExportDialogOpen}
          onClose={handleCloseExportDialog}
          onSubmit={onSubmit}
        />
      </form>
      <NoFilterDataDialog
        open={openNoFilterDialog}
        setOpen={setOpenNoFilterDialog}
      />
    </Box>
  )
}

RipdForm.propTypes = {
  ropa: PropTypes.bool,
  ripd: PropTypes.bool,
  setTriggerValidation: PropTypes.func,
  setDisableGenerateButton: PropTypes.func,
}

RipdForm.defaultProps = {
  ropa: false,
  setTriggerValidation: () => {},
  setDisableGenerateButton: () => {},
}

export default RipdForm
